import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/apiClient';
import { MapPin, Wifi, WifiOff, Loader2, Shield, Clock, ArrowUp, ArrowDown } from 'lucide-react';

const AbogadoLocationLogin = ({ abogadoNameProp }) => {
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coords, setCoords] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const intervalRef = useRef(null);

  const sendLocation = useCallback(async (position) => {
    try {
      const { latitude, longitude } = position.coords;
      const payload = {
        lat: Number(parseFloat(latitude).toFixed(8)),
        lng: Number(parseFloat(longitude).toFixed(8))
      };
      setCoords(payload);
      await api.post('/geolocation/update', payload);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) setError('Sesion expirada. Inicie sesion de nuevo.');
      else setError('Error enviando ubicacion.');
    }
  }, []);

  const startTracking = useCallback(() => {
    navigator.geolocation.getCurrentPosition(sendLocation, () => setError('Active el GPS del dispositivo'), { enableHighAccuracy: true, timeout: 10000 });
    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(sendLocation, null, { enableHighAccuracy: true, timeout: 10000 });
    }, 15000);
  }, [sendLocation]);

  const stopTracking = useCallback(async () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    try { await api.post('/geolocation/disable', {}); } catch { /* silencioso */ }
    setActive(false);
    setCoords(null);
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get('/geolocation/history');
      const arr = Array.isArray(res) ? res : (res.data || []);
      setHistory(arr);
    } catch { /* silencioso */ }
    finally { setLoadingHistory(false); }
  }, []);

  // Verificar estado al montar + cargar historial
  useEffect(() => {
    const init = async () => {
      try {
        const res = await api.get('/geolocation/status');
        if (res.enabled) {
          setActive(true);
          if (res.lat && res.lng) setCoords({ lat: res.lat, lng: res.lng });
          startTracking();
        }
      } catch { /* sin sesion */ }
      finally { setLoading(false); }
    };
    init();
    fetchHistory();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLabor = async () => {
    setLoading(true);
    setError(null);
    try {
      if (active) {
        await stopTracking();
      } else {
        setActive(true);
        startTracking();
      }
      // Recargar historial tras cambio
      setTimeout(fetchHistory, 1000);
    } catch (err) {
      setError(err.message || 'Error de conexion');
    } finally { setLoading(false); }
  };

  const formatTime = (iso) => {
    // Backend guarda UTC, agregar Z si no tiene timezone para que JS lo interprete como UTC
    const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Bogota' });
  };

  if (loading && !active) {
    return (
      <div className="p-4 md:p-8 bg-slate-100 dark:bg-gray-950 min-h-full flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-slate-100 dark:bg-gray-950 min-h-full">
      <div className="max-w-lg mx-auto space-y-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <MapPin size={24} className="text-blue-500" /> Geolocalizacion
        </h1>

        {/* Card principal */}
        <div className={`bg-white dark:bg-gray-800 rounded-2xl border-2 transition-all overflow-hidden ${active ? 'border-green-400 dark:border-green-600 shadow-lg shadow-green-500/10' : 'border-gray-200 dark:border-gray-700'}`}>
          <div className={`px-5 py-4 flex items-center justify-between ${active ? 'bg-green-50 dark:bg-green-900/10' : 'bg-gray-50 dark:bg-gray-800'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-400'}`}>
                {active ? <Wifi size={20} className="animate-pulse" /> : <WifiOff size={20} />}
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 dark:text-white">
                  {active ? 'Transmitiendo ubicacion' : 'GPS inactivo'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {active ? abogadoNameProp : 'Presione iniciar para activar'}
                </p>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-[10px] font-semibold ${active ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
              {active ? 'Activo' : 'Inactivo'}
            </div>
          </div>

          {active && coords && (
            <div className="px-5 py-3 border-t border-green-200 dark:border-green-800/30 bg-green-50/50 dark:bg-green-900/5">
              <div className="flex items-center gap-2 text-xs">
                <MapPin size={12} className="text-green-600" />
                <span className="text-green-700 dark:text-green-400 font-mono">{coords.lat}, {coords.lng}</span>
              </div>
            </div>
          )}

          <div className="p-5">
            <button onClick={toggleLabor} disabled={loading}
              className={`w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all active:scale-95 shadow-sm ${active
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
              {loading ? <Loader2 className="animate-spin" size={18} /> : active ? <><WifiOff size={16} /> Detener transmision</> : <><Wifi size={16} /> Iniciar jornada</>}
            </button>
            {error && (
              <div className="mt-3 p-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-xl text-xs text-red-600 dark:text-red-400 text-center">{error}</div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <Shield size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-gray-500 dark:text-gray-400">
              <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Informacion</p>
              <ul className="space-y-1">
                <li>La ubicacion se actualiza cada 15 segundos</li>
                <li>El administrador puede ver su posicion en tiempo real</li>
                <li>Al detener, se guarda su ultimo punto conocido</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Historial */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock size={16} className="text-blue-500" /> Historial de actividad
            </h3>
            <button onClick={fetchHistory} disabled={loadingHistory} className="text-[10px] text-blue-500 hover:underline">
              {loadingHistory ? <Loader2 size={12} className="animate-spin" /> : 'Actualizar'}
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto custom-scrollbar divide-y divide-gray-50 dark:divide-gray-700/50">
            {history.length === 0 ? (
              <div className="py-8 text-center text-xs text-gray-400">Sin registros de actividad</div>
            ) : (
              history.map((log, i) => {
                const isStart = log.action === 'start';
                return (
                  <div key={i} className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isStart ? 'bg-green-100 dark:bg-green-900/20 text-green-600' : 'bg-red-100 dark:bg-red-900/20 text-red-500'}`}>
                      {isStart ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                        {isStart ? 'Jornada iniciada' : 'Jornada detenida'}
                      </p>
                      <p className="text-[10px] text-gray-400 font-mono truncate">
                        {log.lat?.toFixed(5)}, {log.lng?.toFixed(5)}
                      </p>
                    </div>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{formatTime(log.timestamp)}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }
      `}</style>
    </div>
  );
};

export default AbogadoLocationLogin;
